import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { CHANNEL_LABEL, UNIT_LABEL, type StockChannel, type Unit } from '@artmozaik/shared';
import Ipucu from '../../ui/Ipucu';
import { miktar as miktarYaz } from './ortak';
import { useDar, useGeriKapat } from '../../ui/mobil';

/**
 * HIZLI STOK DÜZELTME — stok sayısına tıklayınca açılan küçük kutu.
 *
 * Mevcut "elle hareket / sayım" ucuna yazar (`POST /api/urunler/:id/hareket`):
 *  · Yeni miktar  → sayım: fark kadar hareket yazılır, geçmiş silinmez
 *  · Ekle / çıkar → elle hareket (+/−)
 * Neden (not) ZORUNLU; kayıt izine düşer.
 */
export default function StokDuzelt({
  urun, kanal: ilkKanal, faturasizYazabilir, kapat, bitti,
}: {
  urun: { id: number; code: string; unit: Unit; official: number; offrec: number | null };
  kanal: StockChannel;
  faturasizYazabilir: boolean;
  kapat: () => void;
  bitti: (sonuc: { kanal: StockChannel; oncesi: number; sonrasi: number }) => void;
}) {
  const [kanal, setKanal] = useState<StockChannel>(ilkKanal);
  const [kip, setKip] = useState<'sayim' | 'hareket'>('sayim');
  const [deger, setDeger] = useState('');
  const [not, setNot] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const kutu = useRef<HTMLFormElement>(null);
  const [yer, setYer] = useState<React.CSSProperties>({ visibility: 'hidden', left: 0, top: 0 });
  /* Dar ekran (telefon + dikey tablet): düğmenin yanında yüzen kutu yerine ekranın altına oturan sayfa
     (klavyenin üstünde kalır, alt gezinme çubuğuna binmez, geri tuşu kapatır, arkadaki sayfa kaymaz).
     Konum CSS'ten gelir; aşağıdaki hesap atlanır. */
  const telefon = useDar();
  useGeriKapat(telefon, kapat);

  // Kutu ekrana SABİT yerleşir (kayan tablo kutusu kırpmasın): açan düğmenin altına, sığmazsa üstüne.
  const yerlestir = useCallback(() => {
    const el = kutu.current; const capa = el?.parentElement;
    if (!el || !capa) return;
    if (telefon) { setYer((eski) => (Object.keys(eski).length === 0 ? eski : {})); return; }
    const c = capa.getBoundingClientRect(); const k = el.getBoundingClientRect();
    const sol = Math.max(8, Math.min(window.innerWidth - k.width - 8, c.right - k.width));
    const altaSigar = c.bottom + 6 + k.height <= window.innerHeight - 8;
    const ust = altaSigar ? c.bottom + 6 : Math.max(8, c.top - 6 - k.height);
    setYer((eski) => (eski.left === sol && eski.top === ust && !eski.visibility ? eski : { left: sol, top: ust }));
  }, [telefon]);
  useLayoutEffect(yerlestir, [yerlestir, kanal, kip]);
  // Sayfa ya da tablo kayınca kutu açan düğmeyi izler.
  useEffect(() => {
    window.addEventListener('scroll', yerlestir, true);
    window.addEventListener('resize', yerlestir);
    return () => { window.removeEventListener('scroll', yerlestir, true); window.removeEventListener('resize', yerlestir); };
  }, [yerlestir]);
  // Odak, kutu yerine oturduktan sonra ve sayfayı KAYDIRMADAN verilir (kaydırma kutuyu kapatır).
  useEffect(() => { kutu.current?.querySelector<HTMLInputElement>('#sd-miktar')?.focus({ preventScroll: true }); }, []);

  const mevcut = kanal === 'OFFICIAL' ? urun.official : urun.offrec ?? 0;
  const sayi = deger.trim() === '' ? null : Number(deger.replace(',', '.'));
  const gecerli = sayi != null && Number.isFinite(sayi);
  const sonrasi = !gecerli ? null : kip === 'sayim' ? sayi! : Math.round((mevcut + sayi!) * 1000) / 1000;

  useEffect(() => {
    // Açan düğmenin kendisi sayılmaz: onun tıklaması zaten aç/kapa yapar.
    const disari = (e: MouseEvent) => { if (kutu.current && !kutu.current.parentElement?.contains(e.target as Node)) kapat(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); kapat(); } };
    // Kutuyu açan tıklama aynı döngüde kapatmasın.
    const t = window.setTimeout(() => document.addEventListener('mousedown', disari), 0);
    document.addEventListener('keydown', esc, true);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', disari); document.removeEventListener('keydown', esc, true); };
  }, [kapat]);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (!gecerli) return setHata('Miktar sayı olmalı');
    if (not.trim().length < 3) return setHata('Neden zorunlu — en az 3 harf');
    setHata(null); setBekliyor(true);
    try {
      const r = await api<{ oncesi: number; sonrasi: number }>(`/api/urunler/${urun.id}/hareket`, {
        method: 'POST', body: JSON.stringify({ channel: kanal, kip, qty: sayi, note: not.trim() }),
      });
      bitti({ kanal, oncesi: r.oncesi, sonrasi: r.sonrasi });
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally { setBekliyor(false); }
  }

  return (
    <form ref={kutu} className={`ccd-sd-kutu${telefon ? ' ccd-sd-kutu-alt' : ''}`} style={yer} onSubmit={gonder} onClick={(e) => e.stopPropagation()}
          role="dialog" aria-label={`${urun.code} stok düzelt`}>
      <div className="ccd-sd-bas">
        <span>Stok düzelt <Ipucu id="hizli-stok-duzeltme" /></span>
        <span className="ccd-mono ccd-muted">{urun.code}</span>
      </div>
      <div className="ccd-sd-secim" role="tablist">
        <button type="button" role="tab" aria-selected={kip === 'sayim'} className={kip === 'sayim' ? 'ccd-is-active' : ''} onClick={() => setKip('sayim')}>Yeni miktar</button>
        <button type="button" role="tab" aria-selected={kip === 'hareket'} className={kip === 'hareket' ? 'ccd-is-active' : ''} onClick={() => setKip('hareket')}>Ekle / çıkar</button>
      </div>
      {urun.offrec != null && faturasizYazabilir && (
        <div className="ccd-field">
          <label className="ccd-field-label" htmlFor="sd-hane">Hane</label>
          <select id="sd-hane" className="ccd-select" value={kanal} onChange={(e) => setKanal(e.target.value as StockChannel)}>
            <option value="OFFICIAL">Resmî ({miktarYaz(urun.official)})</option>
            <option value="OFFREC">Faturasız ({miktarYaz(urun.offrec)})</option>
          </select>
        </div>
      )}
      <div className="ccd-field">
        <label className="ccd-field-label" htmlFor="sd-miktar">
          {kip === 'sayim' ? 'Raftaki gerçek miktar' : 'Değişim (+ giriş, − çıkış)'} · {UNIT_LABEL[urun.unit]}
        </label>
        <input id="sd-miktar" className="ccd-input" inputMode="decimal" autoComplete="off" value={deger}
               placeholder={kip === 'sayim' ? miktarYaz(mevcut) : '+5 ya da -2'} onChange={(e) => setDeger(e.target.value)} />
        <div className="ccd-field-hint">
          {CHANNEL_LABEL[kanal]}: {miktarYaz(mevcut)}{sonrasi != null && sonrasi !== mevcut ? ` → ${miktarYaz(sonrasi)}` : ''}
        </div>
      </div>
      <div className="ccd-field">
        <label className="ccd-field-label" htmlFor="sd-not">Neden<span className="ccd-required"> *</span></label>
        <input id="sd-not" className="ccd-input" value={not} maxLength={300} autoComplete="off"
               placeholder="kırık, sayım farkı, elden çıkış…" onChange={(e) => setNot(e.target.value)} />
      </div>
      {hata && <div className="ccd-sd-hata" role="alert">{hata}</div>}
      <div className="ccd-sd-alt">
        <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Vazgeç</button>
        <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </div>
    </form>
  );
}
